import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  ShoppingCart,
  Description,
  ArrowForward,
  People,
  TrendingUp,
  Today,
} from '@mui/icons-material';
import quotationService from '../../services/quotation.service';
import salesOrderService from '../../services/sales-order.service';
import contactService from '../../services/contact.service';
import invoiceService from '../../services/invoice.service';

const SalesOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalQuotations: 0,
    totalSalesOrders: 0,
    totalContacts: 0,
    todayInvoices: 0,
    recentQuotations: [] as any[],
    recentSalesOrders: [] as any[],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];
        const todayStart = new Date(today).toISOString();
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        const todayEndISO = todayEnd.toISOString();

        const [quotationsResponse, salesOrdersResponse, contactsResponse, invoicesTodayResponse, recentQuotationsResponse, recentSalesOrdersResponse] = await Promise.all([
          quotationService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          salesOrderService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          contactService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          invoiceService.getAll({
            'pagination[pageSize]': 1,
            'filters[createdAt][$gte]': todayStart,
            'filters[createdAt][$lte]': todayEndISO,
          }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          quotationService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'createdAt:desc' }).catch(() => ({ data: { data: [] } })),
          salesOrderService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'createdAt:desc' }).catch(() => ({ data: { data: [] } })),
        ]);

        setStats({
          totalQuotations: quotationsResponse.data?.meta?.pagination?.total || 0,
          totalSalesOrders: salesOrdersResponse.data?.meta?.pagination?.total || 0,
          totalContacts: contactsResponse.data?.meta?.pagination?.total || 0,
          todayInvoices: invoicesTodayResponse.data?.meta?.pagination?.total || 0,
          recentQuotations: recentQuotationsResponse.data?.data || [],
          recentSalesOrders: recentSalesOrdersResponse.data?.data || [],
        });
      } catch (error) {
        console.error('Error fetching sales data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const StatCard = ({ title, value, icon, color, onClick }: any) => (
    <Card elevation={2} sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 6 } : {} }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>{value}</Typography>
        <Typography color="text.secondary" variant="body2">{title}</Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Sales & CRM Overview</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>{t('sales.overview')}</Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
          <StatCard title={t('sales.totalQuotations')} value={stats.totalQuotations} icon={<Description />} color="primary.main" onClick={() => navigate('/quotations')} />
          <StatCard title={t('sales.totalSalesOrders')} value={stats.totalSalesOrders} icon={<ShoppingCart />} color="info.main" onClick={() => navigate('/sales-orders')} />
          <StatCard title={t('sales.totalContacts')} value={stats.totalContacts} icon={<People />} color="success.main" onClick={() => navigate('/contacts')} />
          <StatCard
            title={t("Today's Invoices")}
            value={stats.todayInvoices}
            icon={<Today />}
            color="secondary.main"
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              navigate(`/invoices?dateFrom=${today}&dateTo=${today}`);
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
          <Card elevation={2}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">{t('sales.recentQuotations')}</Typography>
              <IconButton size="small" onClick={() => navigate('/quotations')}><ArrowForward /></IconButton>
            </Box>
            <CardContent>
              <List>
                {stats.recentQuotations.length > 0 ? (
                  stats.recentQuotations.map((quotation: any, index: number) => (
                    <React.Fragment key={quotation.id}>
                      <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/quotations/${quotation.id}`)}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1">{quotation.attributes?.quotationNumber || `QT-${quotation.id}`}</Typography>
                              <Chip label={quotation.attributes?.status || 'draft'} size="small" color={quotation.attributes?.status === 'approved' ? 'success' : 'default'} />
                            </Box>
                          }
                          secondary={`Total: OMR ${quotation.attributes?.totalAmount?.toFixed(2) || '0.00'}`}
                        />
                      </ListItem>
                      {index < stats.recentQuotations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem><ListItemText primary="No recent quotations" /></ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">{t('sales.recentSalesOrders')}</Typography>
              <IconButton size="small" onClick={() => navigate('/sales-orders')}><ArrowForward /></IconButton>
            </Box>
            <CardContent>
              <List>
                {stats.recentSalesOrders.length > 0 ? (
                  stats.recentSalesOrders.map((order: any, index: number) => (
                    <React.Fragment key={order.id}>
                      <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/sales-orders/${order.id}`)}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1">{order.attributes?.orderNumber || `SO-${order.id}`}</Typography>
                              <Chip label={order.attributes?.status || 'pending'} size="small" color={order.attributes?.status === 'confirmed' ? 'success' : 'default'} />
                            </Box>
                          }
                          secondary={`Total: OMR ${order.attributes?.totalAmount?.toFixed(2) || '0.00'}`}
                        />
                      </ListItem>
                      {index < stats.recentSalesOrders.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem><ListItemText primary="No recent sales orders" /></ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/quotations')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">{t('sales.quotations')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/sales-orders')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShoppingCart sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
              <Typography variant="h6">{t('sales.salesOrders')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/contacts')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h6">{t('sales.contacts')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/contact-groups')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6">{t('sales.contactGroups')}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
  );
};

export default SalesOverview;

